import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Spinner, Badge } from 'react-bootstrap';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import { useIncompleteSessions } from 'hooks/useDashboard';
import { formatMoney } from 'helpers/utils';
import paths from 'routes/paths';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { toast } from 'react-toastify';
import { supabase } from 'lib/supabase';

dayjs.extend(relativeTime);

const getReminderStatus = (count) => {
  if (!count || count === 0) return { label: 'No reminders', variant: 'secondary' };
  if (count === 1) return { label: '1st reminder', variant: 'warning' };
  if (count === 2) return { label: 'Final reminder', variant: 'info' };
  return { label: `${count} reminders`, variant: 'primary' };
};

const IncompleteDonations = ({ limit = 10 }) => {
  const { sessions, isLoading, refetch } = useIncompleteSessions();
  const [sendingId, setSendingId] = useState(null);

  const handleSendEmail = async (sessionId) => {
    setSendingId(sessionId);
    try {
      const { data, error } = await supabase.functions.invoke('send-payment-reminder', {
        body: { session_id: sessionId }
      });

      if (error) throw error;

      if (data?.success) {
        toast.success('Email sent successfully');
        refetch();
      } else {
        toast.error(data?.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setSendingId(null);
    }
  };

  const columns = useMemo(() => [
    {
      accessor: 'name',
      Header: 'Donor',
      cellProps: {
        className: 'py-2'
      }
    },
    {
      accessor: 'items',
      Header: 'Items',
      cellProps: {
        className: 'py-2'
      }
    },
    {
      accessor: 'created_at',
      Header: 'Date',
      cellProps: {
        className: 'fw-medium py-2'
      },
      Cell: rowData => {
        const { created_at } = rowData.row.original;
        return dayjs(created_at).fromNow();
      }
    },
    {
      accessor: 'amount',
      Header: 'Amount',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { amount } = rowData.row.original;
        return formatMoney(amount);
      }
    },
    {
      accessor: 'reminder_count',
      Header: 'Status',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { reminder_count, email_sent_at } = rowData.row.original;
        const status = getReminderStatus(reminder_count);
        return (
          <div>
            <Badge bg={status.variant} className="me-1">
              {status.label}
            </Badge>
            {email_sent_at && (
              <small className="text-muted d-block mt-1">
                Last: {dayjs(email_sent_at).fromNow()}
              </small>
            )}
          </div>
        );
      }
    },
    {
      accessor: 'action',
      Header: '',
      disableSortBy: true,
      cellProps: {
        className: 'py-2',
        style: { width: '100px' }
      },
      Cell: rowData => {
        const { id, reminder_count } = rowData.row.original;
        const isSending = sendingId === id;
        const hasReminders = reminder_count > 0;

        return (
          <Button
            variant={hasReminders ? 'falcon-secondary' : 'falcon-primary'}
            size="sm"
            onClick={() => handleSendEmail(id)}
            disabled={isSending}
          >
            {isSending ? <Spinner size="sm" /> : hasReminders ? 'Resend' : 'Send'}
          </Button>
        );
      }
    }
  ], [sendingId]);

  const tableData = useMemo(() => {
    if (!sessions) return [];

    return sessions.slice(0, limit).map(session => {
      // Get donor name
      const donorName = session.donar
        ? `${session.donar.first_name || ''} ${session.donar.last_name || ''}`.trim()
        : 'Unknown';

      // Parse basket items
      let itemsDisplay = '-';
      try {
        const basket = typeof session.basket === 'string'
          ? JSON.parse(session.basket)
          : session.basket;

        if (Array.isArray(basket) && basket.length > 0) {
          itemsDisplay = (
            <ul className="list-unstyled mb-0">
              {basket.map((item, index) => (
                <li key={index}>{item.packageName || 'Item'}</li>
              ))}
            </ul>
          );
        }
      } catch (e) {
        itemsDisplay = '-';
      }

      return {
        name: session.donar ? (
          <Link to={paths.donorDetails.replace(':id', session.donar.id)}>
            {donorName}
          </Link>
        ) : (
          donorName
        ),
        items: itemsDisplay,
        created_at: session.created_at,
        amount: session.total_amount || session.subtotal || 0,
        id: session.id,
        reminder_count: session.reminder_count || 0,
        email_sent_at: session.email_sent_at,
        donar_id: session.donar?.id
      };
    });
  }, [sessions, limit]);

  return (
    <CustomAdvanceTable
      data={tableData}
      columns={columns}
      loading={isLoading}
      title="Incomplete Donations"
      pagination={{
        showInfo: false,
        showNavButtons: false
      }}
    />
  );
};

export default IncompleteDonations;
